import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Grid, 
  Typography, 
  TableContainer, 
  Table, 
  TableBody, 
  TableCell, 
  TableRow, 
  TableHead, 
  Paper, 
  Box,
  Button,
  Divider,
  Tooltip 
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { themeColors, getSatisfactionColor, serviceQualityQuestions, sqdDimensions } from '../common/constants';
import VisibilityIcon from '@mui/icons-material/Visibility';
import surveyApi from '../../services/surveyApi';

// Helper function to format time from milliseconds to minutes
const formatTimeInMinutes = (milliseconds) => {
if (!milliseconds || isNaN(milliseconds)) return 'N/A';

// Convert to minutes and round to 1 decimal place
const minutes = (milliseconds / 60000).toFixed(1);

// Return formatted string
return `${minutes} min${minutes === '1.0' ? '' : 's'}`;
};

// Helper function to render satisfaction emoji based on satisfaction value
const renderSatisfactionEmoji = (satisfaction) => {
switch(satisfaction) {
  case 'Strongly Agree': return '😊';
  case 'Agree': return '🙂';
  case 'Neutral': return '😐';
  case 'Disagree': return '🙁';
  case 'Strongly Disagree': return '😠';
  default: return '';
}
};

const ViewRespondentDialog = ({ open, onClose, respondent }) => {

  if (!respondent) return null;

  // Format creation date if available
  const formattedDate = respondent.created_at 
    ? new Date(respondent.created_at).toLocaleString() 
    : 'N/A';
  
  // Format time spent
  const formattedTimeSpent = formatTimeInMinutes(respondent.timeSpent);
  
  // Handle export of respondent details
  const handleExport = () => {
    // Create a formatted text representation of the respondent data
    const exportData = {
      id: respondent.id,
      date: formattedDate,
      personal_info: {
        InternalExternal: respondent.InternalExternal || 'N/A',
        client_type: respondent.client_type || respondent.clientType || 'N/A',
        gender: respondent.gender || 'N/A',
        age: respondent.age || 'N/A',
        region: respondent.region || 'N/A',
        email: respondent.email || 'N/A'
      },
      transaction_details: {
        office: respondent.office || 'N/A',
        sub_office: respondent.subOffice || 'N/A',
        service: respondent.service || 'N/A',
        other_specification: respondent.other_specification || 'N/A',
        time_spent: formattedTimeSpent
      },
      citizens_charter: {
        cc_awareness: respondent.cc_awareness || 'N/A',
        cc_visibility: respondent.cc_visibility || 'N/A',
        cc_helpfulness: respondent.cc_helpfulness || 'N/A'
      },
      satisfaction: {}
    };
    
    // Add all SQD ratings
    serviceQualityQuestions.forEach(question => {
      const key = question.id;
      exportData.satisfaction[key] = respondent[key] || 'N/A';
    });
    
    // Add overall satisfaction and comments
    exportData.overall_satisfaction = respondent.satisfaction || 'N/A';
    exportData.satisfaction_rate = respondent.satisfaction_rate || 'N/A';
    exportData.suggestions = respondent.suggestions || 'No suggestions provided';
    
    // Convert to JSON and create download link
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `respondent_${respondent.id}_details.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: themeColors.primary, color: 'white' }}>
        Respondent Details - ID: {respondent.id}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Personal Information</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Client Type</TableCell>
                    <TableCell component="th" scope="row" sx={{ textTransform: "uppercase", color:'#800000', fontWeight: 'bold' }} >{respondent.InternalExternal || respondent.InternalExternal || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    
                    <TableCell>{respondent.client_type || respondent.clientType || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                    <TableCell>{respondent.gender || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Age</TableCell>
                    <TableCell>{respondent.age || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Region</TableCell>
                    <TableCell>{respondent.region || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell> 
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">
                           {respondent.email || 'N/A'}
                      </Typography>
                    </Box>  
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Transaction Details</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell>{formattedDate}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Office</TableCell>
                    <TableCell>{respondent.office || 'N/A'}</TableCell>
                  </TableRow>
                  {respondent.subOffice && (
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Sub Office</TableCell>
                      <TableCell>{respondent.subOffice}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Service</TableCell>
                    <TableCell>{respondent.service || 'N/A'}</TableCell>
                  </TableRow>
                  {respondent.other_specification && (
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Service Details</TableCell>
                      <TableCell>{respondent.other_specification}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Time Spent</TableCell>
                    <TableCell>{formattedTimeSpent}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Overall Rating</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        color: getSatisfactionColor(respondent.satisfaction_rate)
                      }}>
                        {renderSatisfactionEmoji(respondent.satisfaction)}
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                          {respondent.satisfaction || 'N/A'}
                          {respondent.satisfaction_rate && (
                            <span style={{ marginLeft: '4px' }}>
                              ({respondent.satisfaction_rate.toFixed(2)})
                            </span>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Citizen's Charter</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>CC Awareness</TableCell>
                    <TableCell>{respondent.cc_awareness || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>CC Visibility</TableCell>
                    <TableCell>{respondent.cc_visibility || 'N/A'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>CC Helpfulness</TableCell>
                    <TableCell>{respondent.cc_helpfulness || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Service Quality Dimensions</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dimension</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Dimension</TableCell>
                    <TableCell>Rating</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[0, 2, 4, 6, 8].map((index) => {
                    const leftQuestion = serviceQualityQuestions[index];
                    const rightQuestion = serviceQualityQuestions[index + 1];
                    
                    if (!leftQuestion) return null;
                    
                    return (
                      <TableRow key={leftQuestion.id}>
                        <TableCell component="th" scope="row">
                          {leftQuestion.label}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: getSatisfactionColor(
                              respondent[leftQuestion.id] ? 
                              (respondent[leftQuestion.id] === 'Strongly Agree' ? 5 : 
                               respondent[leftQuestion.id] === 'Agree' ? 4 :
                               respondent[leftQuestion.id] === 'Neutral' ? 3 :
                               respondent[leftQuestion.id] === 'Disagree' ? 2 :
                               respondent[leftQuestion.id] === 'Strongly Disagree' ? 1 : 0) : 0
                            )
                          }}>
                            {renderSatisfactionEmoji(respondent[leftQuestion.id])}
                            <Typography variant="body2" sx={{ ml: 0.5 }}>
                              {respondent[leftQuestion.id] || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        {rightQuestion && (
                          <>
                            <TableCell component="th" scope="row">
                              {rightQuestion.label}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: getSatisfactionColor(
                                  respondent[rightQuestion.id] ? 
                                  (respondent[rightQuestion.id] === 'Strongly Agree' ? 5 : 
                                   respondent[rightQuestion.id] === 'Agree' ? 4 :
                                   respondent[rightQuestion.id] === 'Neutral' ? 3 :
                                   respondent[rightQuestion.id] === 'Disagree' ? 2 :
                                   respondent[rightQuestion.id] === 'Strongly Disagree' ? 1 : 0) : 0
                                )
                              }}>
                                {renderSatisfactionEmoji(respondent[rightQuestion.id])}
                                <Typography variant="body2" sx={{ ml: 0.5 }}>
                                  {respondent[rightQuestion.id] || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                  {serviceQualityQuestions.length % 2 !== 0 && (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {serviceQualityQuestions[serviceQualityQuestions.length - 1].label}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          color: getSatisfactionColor(
                            respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] ? 
                            (respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] === 'Strongly Agree' ? 5 : 
                             respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] === 'Agree' ? 4 :
                             respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] === 'Neutral' ? 3 :
                             respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] === 'Disagree' ? 2 :
                             respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] === 'Strongly Disagree' ? 1 : 0) : 0
                          )
                        }}>
                          {renderSatisfactionEmoji(respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id])}
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {respondent[serviceQualityQuestions[serviceQualityQuestions.length - 1].id] || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Suggestions/Comments</Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                minHeight: 80, 
                bgcolor: '#f9f9f9',
                borderColor: themeColors.borderColor
              }}
            >
              <Typography variant="body2">
                {respondent.suggestions || "No suggestions provided."}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
        variant="contained"
          onClick={onClose}
          sx={{ 
            bgcolor: themeColors.primary,
            '&:hover': {
              bgcolor: themeColors.primaryLight
            }
          }}
        >
          Close
        </Button>
       
      </DialogActions>
    </Dialog>
  );
};

export default ViewRespondentDialog;